import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Checkbox } from "../ui/checkbox"
import { useApi } from '../../utils/api'
import { getDocument, GlobalWorkerOptions, version } from 'pdfjs-dist';
import { FileIcon, FileTextIcon, ImageIcon } from 'lucide-react';

// Initialize PDF.js worker
if (typeof window !== 'undefined') {
  GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.js`;
}

interface ContentGroup {
  id: number;
  title: string;
}

interface Attachment {
  id?: number;
  s3_key: string;
  name: string;
  fileSize: number;
  fileType?: string;
  sequence: number;
  url?: string;
  height?: number;
  width?: number;
  watermark_top_x_offset?: number;
  watermark_top_y_offset?: number;
  watermark_top_exclude_pages?: string;
  watermark_side_x_offset?: number;
  watermark_side_y_offset?: number;
  watermark_side_exclude_pages?: string;
  content_groups?: ContentGroup[];
  content_group_ids?: number[];
}

interface AttachmentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (attachmentData: Attachment) => void;
  editingAttachment?: Attachment | null;
}

export function AttachmentUploadModal({ isOpen, onClose, onSubmit, editingAttachment }: AttachmentUploadModalProps) {
  const { fetchWithAuth } = useApi();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [contentGroups, setContentGroups] = useState<ContentGroup[]>([]);
  const [selectedContentGroups, setSelectedContentGroups] = useState<number[]>([]);
  const [dimensions, setDimensions] = useState({ height: '', width: '' });
  const [topWatermark, setTopWatermark] = useState({
    x_offset: '0',
    y_offset: '0',
    exclude_pages: ''
  });
  const [sideWatermark, setSideWatermark] = useState({
    x_offset: '0',
    y_offset: '0',
    exclude_pages: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form with editing attachment data
  useEffect(() => {
    if (editingAttachment) {
      setSelectedContentGroups(editingAttachment.content_group_ids || []);
      setDimensions({
        height: editingAttachment.height?.toString() || '',
        width: editingAttachment.width?.toString() || ''
      });
      setTopWatermark({
        x_offset: editingAttachment.watermark_top_x_offset?.toString() || '0',
        y_offset: editingAttachment.watermark_top_y_offset?.toString() || '0',
        exclude_pages: editingAttachment.watermark_top_exclude_pages || ''
      });
      setSideWatermark({
        x_offset: editingAttachment.watermark_side_x_offset?.toString() || '0',
        y_offset: editingAttachment.watermark_side_y_offset?.toString() || '0',
        exclude_pages: editingAttachment.watermark_side_exclude_pages || ''
      });
    }
  }, [editingAttachment]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!isOpen) {
      if (!editingAttachment) {
        setSelectedFile(null);
      }
      setSelectedContentGroups([]);
      setDimensions({ height: '', width: '' });
      setTopWatermark({ x_offset: '0', y_offset: '0', exclude_pages: '' });
      setSideWatermark({ x_offset: '0', y_offset: '0', exclude_pages: '' });
    }
  }, [isOpen, editingAttachment]);

  useEffect(() => {
    const fetchContentGroups = async () => {
      try {
        const response = await fetchWithAuth('/admin/content-groups');
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setContentGroups(data);
          } else {
            console.error('Invalid content groups data structure:', data);
            setContentGroups([]);
          }
        }
      } catch (error) {
        console.error('Error fetching content groups:', error);
        setContentGroups([]);
      }
    };

    if (isOpen) {
      fetchContentGroups();
    }
  }, [isOpen, fetchWithAuth]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);

      if (file.type.startsWith('image/')) {
        const img = new Image();
        img.onload = () => {
          // Convert pixels to inches (assuming 96 DPI)
          const widthInches = (img.width / 96).toFixed(1);
          const heightInches = (img.height / 96).toFixed(1);
          setDimensions({ width: widthInches, height: heightInches });
        };
        img.src = URL.createObjectURL(file);
      } else if (file.type === 'application/pdf') {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await getDocument({ data: arrayBuffer }).promise;
          const page = await pdf.getPage(1);
          const viewport = page.getViewport({ scale: 1 });
          
          // PDF dimensions are in points (1/72 inch)
          const widthInches = (viewport.width / 72).toFixed(1);
          const heightInches = (viewport.height / 72).toFixed(1);
          setDimensions({ width: widthInches, height: heightInches });
        } catch (error) {
          console.error('Error reading PDF dimensions:', error);
          setDimensions({ width: '', height: '' });
        }
      }
    }
  };

  const handleContentGroupToggle = (groupId: number) => {
    setSelectedContentGroups(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  const renderContentGroups = () => (
    <div className="space-y-2">
      {contentGroups.map((group) => (
        <div key={group.id} className="flex items-center space-x-2">
          <Checkbox
            id={`content-group-${group.id}`}
            checked={selectedContentGroups.includes(group.id)}
            onCheckedChange={() => handleContentGroupToggle(group.id)}
          />
          <Label
            htmlFor={`content-group-${group.id}`}
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            onClick={() => handleContentGroupToggle(group.id)}
          >
            {group.title}
          </Label>
        </div>
      ))}
    </div>
  );

  // Show file info when editing
  const getFileDisplay = () => {
    if (selectedFile) {
      return {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type
      };
    }
    if (editingAttachment) {
      return {
        name: editingAttachment.name,
        size: editingAttachment.fileSize,
        type: editingAttachment.fileType
      };
    }
    return null;
  };

  const getFileIcon = () => {
    const fileInfo = getFileDisplay();
    if (!fileInfo) return <FileIcon className="h-8 w-8 text-gray-400" />;
    if (fileInfo.type === 'application/pdf') return <FileTextIcon className="h-8 w-8 text-red-500" />;
    if (fileInfo.type?.startsWith('image/')) return <ImageIcon className="h-8 w-8 text-blue-500" />;
    return <FileIcon className="h-8 w-8 text-gray-400" />;
  };

  const handleSubmit = async () => {
    if (!selectedFile && !editingAttachment) return;

    setIsLoading(true);
    try {
      let attachmentData: Attachment;

      if (selectedFile) {
        // Upload the new file first
        const formData = new FormData();
        formData.append('file', selectedFile);

        const uploadResponse = await fetchWithAuth('/admin/email-templates/attachments', {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload file');
        }

        const data = await uploadResponse.json();
        console.log('Upload response:', data);

        // Create new attachment data with the uploaded file info and existing metadata
        attachmentData = {
          id: data.data.id,
          s3_key: data.data.s3_key,
          name: data.data.name || selectedFile.name,
          fileSize: selectedFile.size,
          fileType: selectedFile.type,
          sequence: editingAttachment?.sequence || 0,
          url: data.data.url,
          content_group_ids: selectedContentGroups,
          content_groups: [],  // This will be populated by the parent component
          height: dimensions.height ? parseFloat(dimensions.height) : undefined,
          width: dimensions.width ? parseFloat(dimensions.width) : undefined,
          watermark_top_x_offset: topWatermark.x_offset ? parseFloat(topWatermark.x_offset) : undefined,
          watermark_top_y_offset: topWatermark.y_offset ? parseFloat(topWatermark.y_offset) : undefined,
          watermark_top_exclude_pages: topWatermark.exclude_pages || undefined,
          watermark_side_x_offset: sideWatermark.x_offset ? parseFloat(sideWatermark.x_offset) : undefined,
          watermark_side_y_offset: sideWatermark.y_offset ? parseFloat(sideWatermark.y_offset) : undefined,
          watermark_side_exclude_pages: sideWatermark.exclude_pages || undefined,
        };

        // If we're editing, we need to delete the old attachment after successful upload
        if (editingAttachment?.id) {
          const deleteResponse = await fetchWithAuth(`/admin/email-templates/attachments/${editingAttachment.id}`, {
            method: 'DELETE',
          });

          if (!deleteResponse.ok) {
            // If delete fails, we should also delete the newly uploaded file to maintain consistency
            await fetchWithAuth(`/admin/email-templates/attachments/${attachmentData.id}`, {
              method: 'DELETE',
            });
            throw new Error('Failed to delete old attachment');
          }
        }
      } else if (editingAttachment) {
        // For editing metadata only (no new file), use existing attachment data
        attachmentData = {
          ...editingAttachment,
          content_group_ids: selectedContentGroups,
          content_groups: [],  // This will be populated by the parent component
          height: dimensions.height ? parseFloat(dimensions.height) : editingAttachment.height,
          width: dimensions.width ? parseFloat(dimensions.width) : editingAttachment.width,
          watermark_top_x_offset: topWatermark.x_offset ? parseFloat(topWatermark.x_offset) : editingAttachment.watermark_top_x_offset,
          watermark_top_y_offset: topWatermark.y_offset ? parseFloat(topWatermark.y_offset) : editingAttachment.watermark_top_y_offset,
          watermark_top_exclude_pages: topWatermark.exclude_pages || editingAttachment.watermark_top_exclude_pages,
          watermark_side_x_offset: sideWatermark.x_offset ? parseFloat(sideWatermark.x_offset) : editingAttachment.watermark_side_x_offset,
          watermark_side_y_offset: sideWatermark.y_offset ? parseFloat(sideWatermark.y_offset) : editingAttachment.watermark_side_y_offset,
          watermark_side_exclude_pages: sideWatermark.exclude_pages || editingAttachment.watermark_side_exclude_pages,
        };
      } else {
        throw new Error('No file selected and no attachment to edit');
      }

      console.log('Submitting attachment data:', attachmentData);
      onSubmit(attachmentData);

      // Reset form
      setSelectedFile(null);
      setSelectedContentGroups([]);
      setDimensions({ height: '', width: '' });
      setTopWatermark({ x_offset: '0', y_offset: '0', exclude_pages: '' });
      setSideWatermark({ x_offset: '0', y_offset: '0', exclude_pages: '' });
      onClose();
    } catch (error) {
      console.error('Error handling attachment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-white rounded-sm shadow-lg border border-gray-200">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            {editingAttachment ? 'Edit Attachment' : 'Upload Attachment'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="flex items-center space-x-4 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50"
                 onClick={() => document.getElementById('file-upload')?.click()}>
              {getFileIcon()}
              <div className="min-w-0 flex-1">
                <div className="flex items-center">
                  <p className="text-sm font-medium text-gray-900 truncate max-w-[300px]">
                    {getFileDisplay()?.name || 'Click to choose a file'}
                  </p>
                </div>
                {getFileDisplay() && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {(getFileDisplay()!.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                )}
              </div>
            </div>
            <input
              id="file-upload"
              type="file"
              onChange={handleFileChange}
              className="hidden"
              accept=".pdf,image/*"
            />
          </div>

          {(selectedFile || editingAttachment) && (
            <>
              <div>
                <Label className="text-sm font-semibold text-panagora-deep-blue mb-2 block">Content Groups</Label>
                <div className="space-y-1 border border-gray-200 rounded-sm p-2">
                  {renderContentGroups()}
                </div>
              </div>

              <div className="h-px bg-gray-200 -mx-6" />

              <div>
                <Label className="text-sm font-semibold text-panagora-deep-blue mb-2 block">Dimensions</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="block text-xs font-semibold text-gray-700 mb-1 uppercase">Width</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={dimensions.width}
                      onChange={(e) => setDimensions(prev => ({ ...prev, width: e.target.value }))}
                      placeholder="8.5"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="block text-xs font-semibold text-gray-700 mb-1 uppercase">Height</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={dimensions.height}
                      onChange={(e) => setDimensions(prev => ({ ...prev, height: e.target.value }))}
                      placeholder="11"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="h-px bg-gray-200 -mx-6" />

              <div>
                <Label className="text-sm font-semibold text-panagora-deep-blue mb-2 block">Top Watermark</Label>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="block text-xs font-semibold text-gray-700 mb-1 uppercase">X Offset</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={topWatermark.x_offset}
                        onChange={(e) => setTopWatermark(prev => ({ ...prev, x_offset: e.target.value }))}
                        placeholder="0"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="block text-xs font-semibold text-gray-700 mb-1 uppercase">Y Offset</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={topWatermark.y_offset}
                        onChange={(e) => setTopWatermark(prev => ({ ...prev, y_offset: e.target.value }))}
                        placeholder="0"
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="block text-xs font-semibold text-gray-700 mb-1 uppercase">Not Displayed on Pages</Label>
                    <Input
                      value={topWatermark.exclude_pages}
                      onChange={(e) => setTopWatermark(prev => ({ ...prev, exclude_pages: e.target.value }))}
                      placeholder="eg. 1,2,4"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="h-px bg-gray-200 -mx-6" />

              <div>
                <Label className="text-sm font-semibold text-panagora-deep-blue mb-2 block">Side Watermark</Label>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="block text-xs font-semibold text-gray-700 mb-1 uppercase">X Offset</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={sideWatermark.x_offset}
                        onChange={(e) => setSideWatermark(prev => ({ ...prev, x_offset: e.target.value }))}
                        placeholder="0"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="block text-xs font-semibold text-gray-700 mb-1 uppercase">Y Offset</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={sideWatermark.y_offset}
                        onChange={(e) => setSideWatermark(prev => ({ ...prev, y_offset: e.target.value }))}
                        placeholder="0"
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="block text-xs font-semibold text-gray-700 mb-1 uppercase">Not Displayed on Pages</Label>
                    <Input
                      value={sideWatermark.exclude_pages}
                      onChange={(e) => setSideWatermark(prev => ({ ...prev, exclude_pages: e.target.value }))}
                      placeholder="eg. 1,2,4"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="h-px bg-gray-200 -mx-6" />
            </>
          )}
        </div>

        <DialogFooter className="sm:justify-end space-x-2 mt-4">
          <Button 
            variant="ghost" 
            onClick={onClose}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-1.5 px-4 rounded-md font-semibold text-sm"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={(!selectedFile && !editingAttachment) || isLoading}
            className="bg-teal-500 hover:bg-teal-400 text-white py-1.5 px-4 rounded-md font-semibold text-sm"
          >
            {isLoading ? 'Saving...' : editingAttachment ? 'Save' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 